import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  Job,
  User,
  UserInput,
  UserRole,
  UserStatus,
} from '../generated/gql-types';
import { userActions } from '../user/user.action';
import { upsertOne } from '../util/zustand.util';
import AsyncStorage from '@react-native-async-storage/async-storage';

interface WorkerState {
  workers: User[];
  isLoading: boolean;
  error: string | null;
  fetchWorkersForTenant: (tenantId: string) => Promise<void>;
  fetchWorkersForClient: (clientId: string) => Promise<void>;
  fetchWorkerById: (workerId: string) => Promise<User | null>;
  fetchDetailedWorkerById: (workerId: string) => Promise<User | null>;
  getWorkerById: (workerId: string) => User | null;
  getWorkersByIds: (workerIds: string[]) => User[];
  getWorkersWorkedForClient: (clientId: string, jobs: Job[]) => User[];
  getWorkersForClassification: (classificationId: string) => User[];
  getWorkerMapping: () => Record<string, User>; // Map of workerId -> worker
  updateWorker: (
    id: string,
    details: Partial<UserInput>,
  ) => Promise<User | null>;
  updateWorkerStatus: (
    workerId: string,
    status: UserStatus,
  ) => Promise<User | null>;
  updateWorkerAssignedAdmin: (
    workerId: string,
    assignedAdminId: string,
    changeReason?: string | undefined,
  ) => Promise<User | null>;
}

export const useWorkerStore = create<WorkerState>()(
  persist(
    (set, get) => ({
      //   state
      workers: [],
      isLoading: false,
      error: null,

      //   actions
      fetchWorkersForTenant: async (tenantId: string) => {
        try {
          set({ isLoading: true });
          const workers = await userActions.fetchWorkersListByTenant(tenantId, [
            UserRole.Worker,
          ]);
          if (workers) {
            set({ workers });
          } else {
            set({ workers: [] });
          }
        } catch (error) {
          console.error('Error fetching workers:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      fetchWorkersForClient: async (clientId: string) => {
        try {
          set({ isLoading: true });
          const workers = await userActions.fetchUsersWorkedForClient(clientId);
          if (workers) {
            set({ workers });
          } else {
            set({ workers: [] });
          }
        } catch (error) {
          console.error('Error fetching workers for client:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      fetchDetailedWorkerById: async (workerId: string) => {
        try {
          set({ isLoading: true });
          const worker = await userActions.fetchUserDetailedById(workerId);

          if (worker) {
            set({
              workers: worker ? upsertOne(get().workers, worker) : [],
            });
          }

          if (!worker) {
            set({ error: 'Worker not found' });
          }

          return worker;
        } catch (error) {
          console.error('Error fetching worker:', error);
          return null;
        } finally {
          set({ isLoading: false });
        }
      },
      fetchWorkerById: async (workerId: string) => {
        try {
          set({ isLoading: true });
          const worker = await userActions.fetchUserById(workerId);

          if (worker) {
            set({
              workers: worker ? upsertOne(get().workers, worker) : [],
            });
          }
          if (!worker) {
            set({ error: 'Worker not found' });
          }
          return worker;
        } catch (error) {
          console.error('Error fetching worker:', error);
          return null;
        } finally {
          set({ isLoading: false });
        }
      },

      //   getters
      getWorkerById: (workerId: string) => {
        return (
          get().workers?.find((worker: User) => worker?.id === workerId) || null
        );
      },
      getWorkersByIds: (workerIds: string[]) => {
        const workerIdSet = new Set(workerIds);
        return get().workers?.filter((worker: User) =>
          workerIdSet.has(worker?.id),
        );
      },
      getWorkersWorkedForClient: (clientId: string, jobs: Job[]) => {
        const workerIds = new Set<string>();
        const clientJobs = jobs.filter(
          (job: Job) => job?.clientId === clientId,
        );
        clientJobs.forEach((job: Job) => {
          if (job.userId && !workerIds.has(job.userId)) {
            workerIds.add(job.userId);
          }
        });
        return get().workers?.filter((worker: User) =>
          workerIds.has(worker?.id),
        );
      },
      getWorkersForClassification: (classificationId: string) => {
        return get().workers?.filter((worker: User) =>
          worker?.classificationLevels?.some(
            (classification) =>
              classification?.classificationId === classificationId,
          ),
        );
      },
      getWorkerMapping: () => {
        return (
          get().workers?.reduce(
            (acc, worker) => {
              acc[worker.id] = worker;
              return acc;
            },
            {} as Record<string, User>,
          ) ?? {}
        );
      },
      updateWorker: async (id: string, details: Partial<UserInput>) => {
        try {
          set({ isLoading: true });

          const updatedWorker = await userActions.updateUser(
            id,
            details as UserInput,
          );

          if (updatedWorker) {
            set({ workers: upsertOne(get().workers, updatedWorker) });
          }

          set({ isLoading: false });

          return updatedWorker;
        } catch (error) {
          console.error('Error updating worker:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      updateWorkerStatus: async (workerId: string, status: UserStatus) => {
        try {
          set({ isLoading: true });
          const updatedUser = await userActions.updateUserStatus(
            workerId,
            status,
          );
          if (updatedUser) {
            set({ workers: upsertOne(get().workers, updatedUser) });
          }
          return updatedUser;
        } catch (error) {
          console.error('Error updating user status:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      updateWorkerAssignedAdmin: async (
        workerId: string,
        assignedAdminId: string,
        changeReason?: string | undefined,
      ) => {
        try {
          set({ isLoading: true });
          const updatedUser = await userActions.updateUserAssignedAdmin(
            workerId,
            assignedAdminId,
            changeReason,
          );
          if (updatedUser) {
            set({ workers: upsertOne(get().workers, updatedUser) });
          }
          return updatedUser;
        } catch (error) {
          console.error('Error updating assigned admin:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'worker-storage', // This is the key used in localStorage
      storage: createJSONStorage(() => AsyncStorage),
      partialize: () => ({}),
    },
  ),
);
